/**
 * PDF Template API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import type { ApiResponse } from '../types';
import type {
  PdfTemplate,
  UpdatePdfTemplateRequest,
  SectionDefinition,
} from '../types/pdfTemplate';

export const pdfTemplateApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get all PDF templates (org-specific with system fallback)
    getPdfTemplates: builder.query<ApiResponse<PdfTemplate[]>, void>({
      query: () => '/pdf-templates',
      providesTags: ['PdfTemplate'],
    }),

    // Get single PDF template by ID
    getPdfTemplate: builder.query<ApiResponse<PdfTemplate>, string>({
      query: id => `/pdf-templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'PdfTemplate', id }],
    }),

    // Get template for a specific module
    getPdfTemplateByModule: builder.query<ApiResponse<PdfTemplate>, string>({
      query: moduleKey => `/pdf-templates/module/${moduleKey}`,
      providesTags: (result, error, moduleKey) => [{ type: 'PdfTemplate', id: moduleKey }],
    }),

    // Get available sections for a module
    getPdfTemplateSections: builder.query<ApiResponse<SectionDefinition[]>, string>({
      query: moduleKey => `/pdf-templates/sections/${moduleKey}`,
    }),

    // Get all available module keys
    getPdfTemplateModules: builder.query<ApiResponse<string[]>, void>({
      query: () => '/pdf-templates/modules',
    }),

    // Update a PDF template
    updatePdfTemplate: builder.mutation<
      ApiResponse<PdfTemplate>,
      { id: string; data: UpdatePdfTemplateRequest }
    >({
      query: ({ id, data }) => ({
        url: `/pdf-templates/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['PdfTemplate'],
    }),

    // Reset template to system default
    resetPdfTemplate: builder.mutation<ApiResponse<PdfTemplate>, string>({
      query: id => ({
        url: `/pdf-templates/${id}/reset`,
        method: 'POST',
      }),
      invalidatesTags: ['PdfTemplate'],
    }),
  }),
});

export const {
  useGetPdfTemplatesQuery,
  useGetPdfTemplateQuery,
  useGetPdfTemplateByModuleQuery,
  useGetPdfTemplateSectionsQuery,
  useGetPdfTemplateModulesQuery,
  useUpdatePdfTemplateMutation,
  useResetPdfTemplateMutation,
} = pdfTemplateApi;
